import { toast } from 'react-toastify';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse } from 'api/ApiClient';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import { HTTPStatusCode } from 'config/const';
import { IAction, ICard, ICreateEmployee, ICreateEmployeeSite, IEmployee, IUpdateEmployee, IUpdateEmployeeSite } from 'types';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../api';
import * as Actions from './actions';
import { IEmployeeMeta } from './actions';
import { Employee } from './types';

export function* getEmployee({ payload }: IAction<Employee, { employeeId: string }>) {
  try {
    const response: ApiResponse<IEmployee> = yield call(API.Employees.show, payload.employeeId);
    yield put(Actions.getEmployeeSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getEmployeeError(error));
  }
}

export function* addEmployee({ payload, meta }: IAction<Employee, { body: ICreateEmployee }, IEmployeeMeta>) {
  try {
    const response: ApiResponse<IEmployee> = yield call(API.Employees.create, payload.body);
    yield put(Actions.addEmployeeSuccess(response.body, meta));

    if (!meta.preventToast) {
      yield call(toast, SuccessToastContainer({ message: `Successfully added ${response.body.name}` }));
    }

    // Execute on success function
    if (meta.onSuccess) yield call(meta.onSuccess, response.body);
  } catch (error: any) {
    let failToast = true;
    const formattedError = formatErrorMessages({
      errors: error.body?.errors,
      statusCode: error.statusCode,
    });
    try {
      if (error.statusCode === HTTPStatusCode.CONFLICT && formattedError.id && meta.onDuplicate) {
        const duplicateEmployeeResponse: ApiResponse<IEmployee> = yield call(API.Employees.show, formattedError.id);
        yield call(meta.onDuplicate, duplicateEmployeeResponse.body);
        failToast = false;
      }
    } finally {
      yield put(Actions.addEmployeeError(formattedError, { failToast }));
    }
  }
}

export function* updateEmployee({ payload, meta }: IAction<Employee, { employeeId: string; body: IUpdateEmployee }, IEmployeeMeta>) {
  try {
    const response: ApiResponse<IEmployee> = yield call(API.Employees.update, payload.employeeId, payload.body);
    yield put(Actions.updateEmployeeSuccess(response.body, meta));

    // Show success toast
    if (meta.successMessage) {
      yield call(toast, SuccessToastContainer({ message: meta.successMessage }));
    }

    // Execute on success function
    if (meta.onSuccess) yield call(meta.onSuccess, response.body);
  } catch (error: any) {
    let failToast = true;
    const formattedError = formatErrorMessages({
      errors: error.body?.errors,
      statusCode: error.statusCode,
    });
    try {
      if (error.statusCode === HTTPStatusCode.CONFLICT && formattedError.id && meta.onDuplicate) {
        const duplicateEmployeeResponse: ApiResponse<IEmployee> = yield call(API.Employees.show, formattedError.id);
        yield call(meta.onDuplicate, duplicateEmployeeResponse.body);
        failToast = false;
      }
    } finally {
      yield put(Actions.updateEmployeeError(formattedError, { ...meta, failToast: meta.failToast || failToast }));
    }
  }
}

export function* addEmployeeToSite({ payload, meta }: IAction<Employee, { employeeId: string; body: ICreateEmployeeSite }, IEmployeeMeta>) {
  try {
    const response: ApiResponse<IEmployee> = yield call(API.Employees.addSite, payload.employeeId, payload.body);
    yield put(Actions.addEmployeeToSiteSuccess(response.body));

    // Show success toast
    if (meta.successMessage) {
      yield call(toast, SuccessToastContainer({ message: meta.successMessage }));
    }

    // Execute on success function
    if (meta.onSuccess) yield call(meta.onSuccess, response.body);
  } catch (error: any) {
    yield put(Actions.addEmployeeToSiteError(error));
  }
}

export function* removeEmployeeFromSite({ payload, meta }: IAction<Employee, { employeeId: string; siteId: string }, { successMessage?: string }>) {
  try {
    const response: ApiResponse<IEmployee> = yield call(API.Employees.removeSite, payload.employeeId, payload.siteId);
    yield put(Actions.removeEmployeeFromSiteSuccess(response.body));

    // Show success toast
    if (meta.successMessage) {
      yield call(toast, SuccessToastContainer({ message: meta.successMessage }));
    }
  } catch (error: any) {
    yield put(Actions.removeEmployeeFromSiteError(error));
  }
}

export function* updateEmployeeSiteRole({ payload, meta }: IAction<Employee, { employeeId: string; siteId: string; body: IUpdateEmployeeSite }, IEmployeeMeta>) {
  try {
    const response: ApiResponse<IEmployee> = yield call(API.Employees.updateSite, payload.employeeId, payload.siteId, payload.body);
    yield put(Actions.updateEmployeeSiteRoleSuccess(response.body));

    // Show success toast
    if (meta.successMessage) {
      yield call(toast, SuccessToastContainer({ message: meta.successMessage }));
    }
    // Execute on success function
    if (meta.onSuccess) yield call(meta.onSuccess, response.body);
  } catch (error: any) {
    yield put(Actions.updateEmployeeSiteRoleError(error));
  }
}

export function* getEmployeeCard({ payload }: IAction<Employee, { employeeId: string; cardId: string }>) {
  try {
    const response: ApiResponse<ICard> = yield call(API.Employees.showCard, payload.employeeId, payload.cardId);
    yield put(Actions.getEmployeeCardSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getEmployeeCardError(error));
  }
}

export function* deleteEmployee({ payload, meta }: IAction<Employee.DELETE_EMPLOYEE_REQUEST, { employeeId: string }, { onSuccess: () => void }>) {
  try {
    const response: ApiResponse<IEmployee> = yield call(API.Employees.delete, payload.employeeId);

    yield put(Actions.deleteEmployeeSuccess(response.body));
    yield call(meta.onSuccess);
    // Show success toast
    yield call(toast, SuccessToastContainer({ message: 'Successfully deleted employee' }));
  } catch (error: any) {
    yield put(Actions.deleteEmployeeError(error));
  }
}

export function* watchGetEmployee() {
  yield takeEvery(Employee.GET_EMPLOYEE_REQUEST, getEmployee);
}

export function* watchAddEmployee() {
  yield takeEvery(Employee.ADD_EMPLOYEE_REQUEST, addEmployee);
}

export function* watchUpdateEmployee() {
  yield takeEvery(Employee.UPDATE_EMPLOYEE_REQUEST, updateEmployee);
}

export function* watchAddEmployeeToSite() {
  yield takeEvery(Employee.ADD_EMPLOYEE_TO_SITE_REQUEST, addEmployeeToSite);
}

export function* watchRemoveEmployeeFromSite() {
  yield takeEvery(Employee.REMOVE_EMPLOYEE_FROM_SITE_REQUEST, removeEmployeeFromSite);
}

export function* watchUpdateEmployeeSiteRole() {
  yield takeEvery(Employee.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST, updateEmployeeSiteRole);
}

export function* watchGetEmployeeCard() {
  yield takeEvery(Employee.GET_EMPLOYEE_CARD_REQUEST, getEmployeeCard);
}

export function* watchDeleteEmployee() {
  yield takeEvery(Employee.DELETE_EMPLOYEE_REQUEST, deleteEmployee);
}

function* employeeSaga() {
  yield all([
    fork(watchGetEmployee),
    fork(watchAddEmployee),
    fork(watchUpdateEmployee),
    fork(watchAddEmployeeToSite),
    fork(watchRemoveEmployeeFromSite),
    fork(watchUpdateEmployeeSiteRole),
    fork(watchGetEmployeeCard),
    fork(watchDeleteEmployee),
  ]);
}
export { employeeSaga };
